import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import api, { formatApiError } from "../lib/api";
import { Link } from "react-router-dom";
import { Search, Plus, Pencil, Trash2, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BLANK = {
  first_name: "",
  last_initial: "",
  email: "",
  phone: "",
  sms_opt_in: false,
  preferred_channel: "email",
  operational_notes: "",
  waiver_status: "none",
};

export default function Clients() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | <id>
  const [draft, setDraft] = useState(BLANK);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const load = () => {
    api.get("/clients", { params: search ? { search } : {} })
      .then(({ data }) => setClients(data));
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const startNew = () => {
    setError(""); setDeleteError("");
    setEditing("new");
    setDraft(BLANK);
  };
  const startEdit = (c) => {
    setError(""); setDeleteError("");
    setEditing(c.id);
    setDraft({
      first_name: c.first_name || "",
      last_initial: c.last_initial || "",
      email: c.email || "",
      phone: c.phone || "",
      sms_opt_in: !!c.sms_opt_in,
      preferred_channel: c.preferred_channel || "email",
      operational_notes: c.operational_notes || "",
      waiver_status: c.waiver_status || "none",
    });
  };
  const cancelEdit = () => { setEditing(null); setError(""); };

  const save = async () => {
    setError("");
    if (!draft.first_name.trim() || !draft.email.trim()) {
      setError("First name and email are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...draft,
        first_name: draft.first_name.trim(),
        last_initial: (draft.last_initial.trim()[0] || "").toUpperCase(),
        email: draft.email.trim().toLowerCase(),
        phone: draft.phone.trim(),
      };
      if (editing === "new") await api.post("/clients", payload);
      else await api.put(`/clients/${editing}`, payload);
      setEditing(null);
      load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const removeClient = async (c) => {
    setDeleteError("");
    if (!window.confirm(`Delete ${c.first_name} ${c.last_initial}.? This only works if they have no appointments, packages, or tincture logs on file.`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      load();
    } catch (e) {
      setDeleteError(`${c.first_name}: ${formatApiError(e)}`);
    }
  };

  const canDelete = user?.role === "admin";

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-sage mb-2">— Clients</p>
          <h1 className="font-display text-4xl text-charcoal">Client directory</h1>
          <p className="text-sm font-ui text-slate mt-1">{clients.length} {clients.length === 1 ? "client" : "clients"} on file</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 text-slate absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
            <input
              type="search"
              placeholder="Search name, email, phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2.5 bg-white border border-hairline rounded-md text-sm font-ui text-charcoal w-72 focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/30"
              data-testid="clients-search"
            />
          </div>
          <button onClick={startNew} className="px-4 py-2 bg-forest hover:bg-charcoal text-white text-sm font-ui rounded-md inline-flex items-center gap-1.5" data-testid="new-client">
            <Plus className="w-4 h-4" /> New client
          </button>
        </div>
      </div>

      {deleteError && (
        <div className="mb-4 px-4 py-3 bg-status-danger-bg/40 border border-status-danger/30 rounded-md text-sm font-ui text-status-danger" data-testid="clients-delete-error">
          {deleteError}
        </div>
      )}

      {editing && (
        <ClientEditor
          draft={draft}
          setDraft={setDraft}
          isNew={editing === "new"}
          error={error}
          busy={busy}
          onCancel={cancelEdit}
          onSave={save}
        />
      )}

      <div className="bg-white border border-hairline rounded-lg shadow-warm overflow-hidden" data-testid="clients-list">
        {clients.length === 0 && (
          <div className="px-6 py-12 text-center text-sm font-ui text-slate">
            No clients yet. Click <strong className="text-forest">New client</strong> to add the first one.
          </div>
        )}
        <ul className="divide-y divide-hairline">
          {clients.map((c) => (
            <li key={c.id} className="flex items-center justify-between px-6 py-4 hover:bg-sand/40 transition-colors group" data-testid={`client-row-${c.id}`}>
              <Link to={`/admin/clients/${c.id}`} className="flex items-center gap-4 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-pill bg-cream flex items-center justify-center text-forest font-medium shrink-0">
                  {c.first_name?.[0]}{c.last_initial?.[0]}
                </div>
                <div className="min-w-0">
                  <p className="font-ui text-sm text-charcoal">{c.first_name} {c.last_initial}.</p>
                  <p className="font-mono text-xs text-slate truncate">{c.email}</p>
                </div>
              </Link>
              <div className="flex items-center gap-3 text-xs font-ui text-slate shrink-0">
                <span className={`px-2 py-1 rounded-pill ${c.waiver_status === "signed" ? "bg-status-success-bg text-status-success" : "bg-status-warning-bg text-status-warning"}`}>
                  waiver: {c.waiver_status}
                </span>
                <span className="hidden md:inline">{c.phone}</span>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => startEdit(c)} className="p-1.5 text-slate hover:text-forest hover:bg-cream rounded-md" title="Edit" data-testid={`edit-client-${c.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  {canDelete && (
                    <button onClick={() => removeClient(c)} className="p-1.5 text-slate hover:text-status-danger hover:bg-status-danger-bg/40 rounded-md" title="Delete" data-testid={`delete-client-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </AdminLayout>
  );
}

function ClientEditor({ draft, setDraft, isNew, error, busy, onCancel, onSave }) {
  return (
    <div className="bg-white border border-hairline rounded-lg shadow-warm p-6 mb-6" data-testid="client-editor">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-ui font-semibold text-charcoal">{isNew ? "New client" : "Edit client"}</h2>
        <button onClick={onCancel} className="p-1 text-slate hover:text-charcoal"><X className="w-4 h-4" /></button>
      </div>
      {error && <div className="mb-3 px-3 py-2 bg-status-danger-bg/40 border border-status-danger/30 rounded-md text-xs font-ui text-status-danger" data-testid="client-form-error">{error}</div>}
      <div className="grid grid-cols-6 gap-4">
        <label className="block col-span-3">
          <span className="block text-xs font-ui text-slate mb-1">First name *</span>
          <input className={inputCls} value={draft.first_name} onChange={(e) => setDraft({ ...draft, first_name: e.target.value })} data-testid="client-first-name" />
        </label>
        <label className="block col-span-3">
          <span className="block text-xs font-ui text-slate mb-1">Last initial</span>
          <input maxLength={1} className={inputCls} value={draft.last_initial} onChange={(e) => setDraft({ ...draft, last_initial: e.target.value })} data-testid="client-last-initial" />
        </label>
        <label className="block col-span-4">
          <span className="block text-xs font-ui text-slate mb-1">Email *</span>
          <input type="email" className={inputCls} value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} data-testid="client-email" />
        </label>
        <label className="block col-span-2">
          <span className="block text-xs font-ui text-slate mb-1">Phone</span>
          <input className={inputCls} value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} data-testid="client-phone" />
        </label>
        <label className="block col-span-2">
          <span className="block text-xs font-ui text-slate mb-1">Preferred channel</span>
          <select className={inputCls} value={draft.preferred_channel} onChange={(e) => setDraft({ ...draft, preferred_channel: e.target.value })} data-testid="client-channel">
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="both">Both</option>
          </select>
        </label>
        <label className="block col-span-2">
          <span className="block text-xs font-ui text-slate mb-1">Waiver status</span>
          <select className={inputCls} value={draft.waiver_status} onChange={(e) => setDraft({ ...draft, waiver_status: e.target.value })} data-testid="client-waiver-status">
            <option value="none">None</option>
            <option value="signed">Signed</option>
            <option value="expired">Expired</option>
          </select>
        </label>
        <label className="col-span-2 inline-flex items-center gap-2 mt-7 text-sm font-ui text-charcoal">
          <input type="checkbox" checked={draft.sms_opt_in} onChange={(e) => setDraft({ ...draft, sms_opt_in: e.target.checked })} className="rounded border-hairline text-forest focus:ring-sage" data-testid="client-sms-opt-in" />
          SMS opt-in
        </label>
        <label className="block col-span-6">
          <span className="block text-xs font-ui text-slate mb-1">Operational notes</span>
          <textarea rows={3} className={inputCls} value={draft.operational_notes} onChange={(e) => setDraft({ ...draft, operational_notes: e.target.value })} placeholder="Allergies, payment preferences, scheduling notes…" data-testid="client-notes" />
        </label>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onCancel} className="px-4 py-2 border border-hairline bg-white text-charcoal text-sm font-ui rounded-md hover:bg-cream">Cancel</button>
        <button onClick={onSave} disabled={busy} className="px-4 py-2 bg-forest hover:bg-charcoal text-white text-sm font-ui rounded-md disabled:opacity-40" data-testid="client-save">
          {busy ? "Saving…" : isNew ? "Create client" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

const inputCls = "w-full px-3 py-2 bg-white border border-hairline rounded-md text-sm font-ui text-charcoal focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/30";
